/**
 * Anti-Gravity Theme - Global JavaScript
 * Premium Shopify Theme JS with AJAX cart, search, and interactions
 */

(function () {
  'use strict';

  // =========================================
  // Theme Configuration
  // =========================================
  window.theme = window.theme || {};
  window.theme.routes = window.routes || {};

  // =========================================
  // Utility Functions
  // =========================================
  const utils = {
    debounce(fn, wait) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    formatMoney(cents) {
      return 'Rs. ' + Math.round(cents / 100).toLocaleString('en-IN') + '.00';
    },

    fetchConfig(type = 'json') {
      return {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': `application/${type}`
        }
      };
    }
  };

  // =========================================
  // Cart Functions
  // =========================================
  const cart = {
    async add(variantId, quantity = 1) {
      const response = await fetch(window.routes.cart_add_url + '.js', {
        ...utils.fetchConfig(),
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (response.ok) {
        const data = await response.json();
        await this.refresh();
        // Go to cart page instead of opening drawer
        window.location.href = window.routes.cart_url || '/cart';
        return data;
      }
      throw new Error('Failed to add to cart');
    },

    async update(key, quantity) {
      const response = await fetch(window.routes.cart_change_url + '.js', {
        ...utils.fetchConfig(),
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (response.ok) {
        const data = await response.json();
        await this.refresh();
        return data;
      }
      throw new Error('Failed to update cart');
    },

    async remove(key) {
      return this.update(key, 0);
    },

    async refresh() {
      const response = await fetch('/cart.js');
      const cartData = await response.json();

      // Update cart count
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cartData.item_count;
        el.style.display = cartData.item_count > 0 ? 'flex' : 'none';
      });

      // Update cart drawer subtotal
      const subtotalEl = document.getElementById('cart-drawer-subtotal');
      if (subtotalEl) {
        subtotalEl.textContent = utils.formatMoney(cartData.total_price);
      }

      // Refresh drawer content via section rendering
      this.refreshDrawerContent();

      return cartData;
    },

    async refreshDrawerContent() {
      try {
        const response = await fetch('/?section_id=cart-drawer');
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const newContent = doc.querySelector('.cart-drawer');
        const currentDrawer = document.querySelector('.cart-drawer');

        if (newContent && currentDrawer) {
          currentDrawer.innerHTML = newContent.innerHTML;
          this.bindEvents();
        }
      } catch (error) {
        console.error('Failed to refresh cart drawer:', error);
      }
    },

    openDrawer() {
      const drawer = document.getElementById('cart-drawer');
      if (drawer) {
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('no-scroll');
        var wrapper = document.getElementById('page-scroll-wrapper');
        if (wrapper) wrapper.style.overflow = 'hidden';
      }
    },

    closeDrawer() {
      const drawer = document.getElementById('cart-drawer');
      if (drawer) {
        drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('no-scroll');
        var wrapper = document.getElementById('page-scroll-wrapper');
        if (wrapper) wrapper.style.overflow = '';
      }
    },

    bindEvents() {
      // Use event delegation on document to avoid stacking listeners on re-render
      if (this._bound) return;
      this._bound = true;

      document.addEventListener('click', async (e) => {
        // Close cart drawer
        var closeBtn = e.target.closest('[data-cart-drawer-close]');
        if (closeBtn) {
          this.closeDrawer();
          return;
        }

        // Quantity change (works for both drawer and cart page)
        var qtyBtn = e.target.closest('[data-quantity-change]');
        if (qtyBtn) {
          e.preventDefault();
          var key = qtyBtn.dataset.quantityChange;
          var action = qtyBtn.dataset.action;
          var quantityEl = qtyBtn.parentElement.querySelector('.quantity-value, .quantity-selector__input');
          var quantity = parseInt(quantityEl.textContent || quantityEl.value);

          if (action === 'decrease' || action === 'minus') {
            quantity = Math.max(0, quantity - 1);
          } else {
            quantity += 1;
          }

          await this.update(key, quantity);
          return;
        }

        // Remove item
        var removeBtn = e.target.closest('[data-remove-item]');
        if (removeBtn) {
          e.preventDefault();
          var removeKey = removeBtn.dataset.removeItem;
          await this.remove(removeKey);
          return;
        }

        // Quick add to cart
        var addBtn = e.target.closest('[data-add-to-cart]');
        if (addBtn) {
          e.preventDefault();
          var variantId = addBtn.dataset.addToCart;
          await this.add(variantId);
          return;
        }
      });
    }
  };

  // =========================================
  // Search Functions
  // =========================================
  const search = {
    openDrawer() {
      const drawer = document.getElementById('search-drawer');
      if (drawer) {
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('no-scroll');
        var wrapper = document.getElementById('page-scroll-wrapper');
        if (wrapper) wrapper.style.overflow = 'hidden';
        const input = drawer.querySelector('[data-predictive-search-input]');
        if (input) input.focus();
      }
    },

    closeDrawer() {
      const drawer = document.getElementById('search-drawer');
      if (drawer) {
        drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('no-scroll');
        var wrapper = document.getElementById('page-scroll-wrapper');
        if (wrapper) wrapper.style.overflow = '';
      }
    },

    async predictiveSearch(query) {
      if (!query || query.length < 2) {
        this.showPopularSearches();
        return;
      }

      const loadingEl = document.getElementById('search-loading');
      const resultsEl = document.getElementById('search-products');
      const gridEl = document.getElementById('search-products-grid');
      const noResultsEl = document.getElementById('search-no-results');
      const popularEl = document.getElementById('popular-searches');

      loadingEl.hidden = false;
      resultsEl.hidden = true;
      noResultsEl.hidden = true;
      popularEl.hidden = true;

      try {
        const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`);
        const data = await response.json();

        loadingEl.hidden = true;

        const products = data.resources?.results?.products || [];

        if (products.length > 0) {
          gridEl.innerHTML = products.map(product => `
            <a href="${product.url}" class="search-product-card">
              <div class="search-product-card__image">
                <img src="${product.featured_image?.url || ''}" alt="${product.title}" loading="lazy">
              </div>
              <h4 class="search-product-card__title">${product.title}</h4>
              <div class="search-product-card__price">${utils.formatMoney(product.price)}</div>
            </a>
          `).join('');
          resultsEl.hidden = false;
        } else {
          document.getElementById('search-query-text').textContent = query;
          noResultsEl.hidden = false;
        }
      } catch (error) {
        console.error('Predictive search error:', error);
        loadingEl.hidden = true;
        this.showPopularSearches();
      }
    },

    showPopularSearches() {
      document.getElementById('popular-searches').hidden = false;
      document.getElementById('search-products').hidden = true;
      document.getElementById('search-no-results').hidden = true;
      document.getElementById('search-loading').hidden = true;
    },

    bindEvents() {
      // Close buttons
      document.querySelectorAll('[data-search-drawer-close]').forEach(el => {
        el.addEventListener('click', () => this.closeDrawer());
      });

      // Search input
      const searchInput = document.querySelector('[data-predictive-search-input]');
      if (searchInput) {
        searchInput.addEventListener('input', utils.debounce((e) => {
          this.predictiveSearch(e.target.value);
        }, 300));
      }
    }
  };

  // =========================================
  // Header Functions
  // =========================================
  const header = {
    init() {
      this.header = document.querySelector('[data-header]');
      if (!this.header) return;

      this.handleScroll();
      window.addEventListener('scroll', () => this.handleScroll());
    },

    handleScroll() {
      const scrollY = window.pageYOffset;

      if (scrollY > 100) {
        this.header.classList.remove('header--transparent');
        this.header.classList.add('header--solid', 'header--scrolled');
      } else {
        this.header.classList.add('header--transparent');
        this.header.classList.remove('header--solid', 'header--scrolled');
      }
    }
  };

  // =========================================
  // Mobile Menu Functions
  // =========================================
  const mobileMenu = {
    open() {
      const menu = document.querySelector('[data-mobile-menu]');
      if (menu) {
        menu.classList.add('mobile-menu--open');
        document.body.classList.add('no-scroll');
        var wrapper = document.getElementById('page-scroll-wrapper');
        if (wrapper) wrapper.style.overflow = 'hidden';
      }
    },

    close() {
      const menu = document.querySelector('[data-mobile-menu]');
      if (menu) {
        menu.classList.remove('mobile-menu--open');
        document.body.classList.remove('no-scroll');
        var wrapper = document.getElementById('page-scroll-wrapper');
        if (wrapper) wrapper.style.overflow = '';
      }
    },

    bindEvents() {
      document.querySelectorAll('[data-menu-toggle]').forEach(el => {
        el.addEventListener('click', () => this.open());
      });

      document.querySelectorAll('[data-menu-close]').forEach(el => {
        el.addEventListener('click', () => this.close());
      });
    }
  };

  // =========================================
  // Back to Top
  // =========================================
  const backToTop = {
    init() {
      const btn = document.querySelector('[data-back-to-top]');
      if (!btn) return;

      window.addEventListener('scroll', () => {
        if (window.pageYOffset > 500) {
          btn.classList.add('back-to-top--visible');
        } else {
          btn.classList.remove('back-to-top--visible');
        }
      });

      btn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  };

  // =========================================
  // Fade In Animation
  // =========================================
  const fadeIn = {
    init() {
      const elements = document.querySelectorAll('[data-fade-in]');
      if (!elements.length) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in--visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      elements.forEach(el => observer.observe(el));
    }
  };

  // =========================================
  // Product Form
  // =========================================
  const productForm = {
    init() {
      // Handle quantity buttons on product pages
      document.querySelectorAll('[data-quantity-minus]').forEach(btn => {
        btn.addEventListener('click', function () {
          const input = this.parentElement.querySelector('.quantity-selector__input');
          const value = parseInt(input.value);
          if (value > 1) input.value = value - 1;
        });
      });

      document.querySelectorAll('[data-quantity-plus]').forEach(btn => {
        btn.addEventListener('click', function () {
          const input = this.parentElement.querySelector('.quantity-selector__input');
          input.value = parseInt(input.value) + 1;
        });
      });

      // Handle add to cart form submissions
      document.querySelectorAll('.add-to-cart-form, .product-form').forEach(form => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          const formData = new FormData(form);
          const variantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity')) || 1;

          try {
            await cart.add(variantId, quantity);
          } catch (error) {
            console.error('Add to cart error:', error);
          }
        });
      });
    }
  };

  // =========================================
  // Initialize
  // =========================================
  document.addEventListener('DOMContentLoaded', function () {
    // Core functionality
    header.init();
    cart.bindEvents();
    search.bindEvents();
    mobileMenu.bindEvents();
    backToTop.init();
    fadeIn.init();
    productForm.init();

    // Open cart page on icon click (both header + bottom nav go to /cart)
    document.querySelectorAll('[data-open-cart]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        window.location.href = window.routes.cart_url || '/cart';
      });
    });

    // Open search on icon click
    document.querySelectorAll('[data-open-search]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        search.openDrawer();
      });
    });

    // Close drawers on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        cart.closeDrawer();
        search.closeDrawer();
        mobileMenu.close();
      }
    });
  });

  // Expose to global scope
  window.theme.cart = cart;
  window.theme.search = search;

})();
